<template>
    <div v-if="isLoaded">
        <div class="page" v-if="offer.products.length === 0">
            <h1>Niciun produs în ofertă</h1>
            <p>
                Nu aveți niciun produs adăugat în oferta în lucru. Puteți începe prin a căuta produsul
                dorit, utilizând căsuța de căutare.
            </p>
        </div>

        <div v-else>
            <div v-if="hasIssues" class="red page">
                <h1>Probleme de integritate</h1>

                <p>
                    Au fost găsite probleme de integritate la această ofertă.
                    Acest lucru înseamnă că informațiile de mai jos nu mai sunt de actualitate.
                </p>

                <p>
                    Vă rugăm să luați la cunoștință aceste erori, și în cazul în care doriți, să le remediați mai jos.
                </p>

                <template v-if="this.issues.changed_currencies.length > 0">
                    <hr />

                    <h2>Cursuri valutare schimbate</h2>
                    <p>
                        Au fost detectate schimbări ale cursurilor valutare utilizate pentru calculul
                        prețurilor din această ofertă:
                    </p>
                    <ul>
                        <li v-for="(info, index) in this.issues.changed_currencies" :key="index">
                            1 {{info.currency}} s-a schimbat din
                            {{parseFloat(info.old_value).toFixed(4)}} RON în
                            {{parseFloat(info.new_value).toFixed(4)}} RON
                        </li>
                    </ul>
                </template>

                <template v-if="this.issues.changed_offer_items.length > 0">
                    <hr />

                    <h2>Informații de stoc actualizate</h2>
                    <p>
                        Au fost detectate schimbări ale informațiilor de stoc utilizate în cadrul acestei oferte:
                    </p>

                    <table class="table table-sm table-borderless table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Denumire produs</th>
                            <th scope="col">Furnizor</th>
                            <th scope="col" class="nowrap">Preț vechi</th>
                            <th scope="col" class="nowrap">Preț nou</th>
                            <th scope="col" class="nowrap">Stoc vechi</th>
                            <th scope="col" class="nowrap">Stoc nou</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(info, index) in this.issues.changed_offer_items" :key="index">
                            <td>{{info.product_name}}</td>
                            <td>{{info.supplier_name}}</td>

                            <template v-if="(info.old_product_stock.price != info.new_product_stock.price) || (info.old_product_stock.currency != info.new_product_stock.currency)">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.price).toFixed(2)}} {{info.old_product_stock.currency}}</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.price).toFixed(2)}} {{info.new_product_stock.currency}}</td>
                            </template>
                            <template v-else-if="info.old_product_stock.ron_price != info.new_product_stock.ron_price">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.ron_price).toFixed(2)}} RON</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.ron_price).toFixed(2)}} RON</td>
                            </template>
                            <template v-else>
                                <td class="nowrap"></td>
                                <td class="nowrap"></td>
                            </template>

                            <template v-if="info.old_product_stock.quantity != info.new_product_stock.quantity">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.quantity).toFixed(0)}}</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.quantity).toFixed(0)}}</td>
                            </template>
                            <template v-else>
                                <td class="nowrap"></td>
                                <td class="nowrap"></td>
                            </template>
                        </tr>
                        </tbody>
                    </table>
                </template>

                <template v-if="this.issues.unavailable_offer_items.length > 0">
                    <hr />

                    <h2>Informații de stoc radiate</h2>
                    <p>
                        Au fost detectate ștergeri ale informațiilor de stoc utilizate în cadrul acestei oferte.
                        Nu s-au putut găsi automat versiuni mai noi ale acestor informații.
                    </p>
                    <p>Produse la care s-au constatat astfel de erori:</p>
                    <ul>
                        <li v-for="(info, index) in this.issues.unavailable_offer_items" :key="index">
                            <strong>{{info.product_name}}</strong>
                        </li>
                    </ul>
                </template>

                <h1>Remediere probleme</h1>
                <p>
                    Puteți încerca să remediați automat problemele utilizând butonul de mai jos. Țineți cont că
                    anumite probleme, în speță informațiile de stoc radiate, nu pot fi reparate automat.
                </p>
                <b-button class="mt-2" variant="outline-light" @click="fixIssues">Remediere automată</b-button>
            </div>

            <div class="page">
                <h1>1. Setează adaosul comercial</h1>

                <h2>Curs de schimb valutar</h2>
                <div v-for="exchangeRate in offer.exchange_rates" :key="exchangeRate.currency" class="currencies">
                    <CurrencyPicker
                            :currency="exchangeRate.currency"
                            :value="exchangeRate.base_value"
                            :markup-type="exchangeRate.markup_type"
                            :markup="exchangeRate.markup"
                            @selected="selectExchangeRate"
                    />
                </div>

                <h2>Adaos comercial implicit</h2>
                <DefaultMarkupPicker
                        :markup-type="offer.offer.default_markup_type"
                        :markup="offer.offer.default_markup"
                        @selected="selectDefaultMarkup"
                />
            </div>


            <div class="page">
                <h1>2. Configurează produsele</h1>

                <b-alert :show="offer.offer.status === 'EDITING'" variant="info">
                    <h4 class="alert-heading">Adaugă produse</h4>
                    <p>
                        Se pare că vrei să editezi această ofertă...<br/>
                        Dacă vrei să adaugi produse în plus, caută-le folosind bara de căutare. La apăsarea
                        butonului <i>Adaugă</i>, vei fi întrebat în care ofertă dorești să adaugi produsul.
                        Trebuie doar să alegi oferta cu numărul <strong>#{{offerId}}</strong>.
                    </p>
                </b-alert>

                <table class="table table-sm table-borderless table-striped">
                    <thead>
                    <tr>
                        <th scope="col">Denumire</th>
                        <th scope="col"></th>
                        <th scope="col">Producător</th>
                        <th scope="col">Furnizor</th>
                        <th scope="col">Preț furnizor</th>
                        <th scope="col">Adaos</th>
                        <th scope="col">Cantitate</th>
                        <th scope="col">Preț final</th>
                        <th scope="col">Profit</th>
                        <th scope="col"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(product, index) in orderedProducts" :key="product.item_id">
                        <td>
                            {{product.name}}
                            <br/>
                            <div class="product-codes">
                                <span v-if="product.codes.product_info.ean">EAN: {{product.codes.product_info.ean}}</span>
                                <span v-if="product.codes.product_info.upc">UPC: {{product.codes.product_info.upc}}</span>
                                <span v-if="product.codes.product_info.oem_codes">
                                <span>OEM: </span>
                                <span v-for="code in product.codes.product_info.oem_codes" :key="code.id">
                                    {{code.oem_code}}
                                </span>
                            </span>
                                <span v-for="code in product.codes.extra_ids" :key="code.value">{{code.id_name}}: {{code.value}}</span>
                            </div>
                        </td>
                        <td>
                            <ItemNameEditor
                                    :default-name="product.default_name"
                                    :custom-name="product.name"
                                    @saveName="editedName => onItemSaveName(product.item_id, editedName)"
                            />
                        </td>
                        <td>{{product.manufacturer}}</td>
                        <td>{{product.supplier_name}}</td>
                        <td class="nowrap">
                            <span>{{parseFloat(product.supplier_price).toFixed(2)}} {{product.supplier_currency}}</span><br/>
                            <span class="small-text">{{parseFloat(product.base_ron_price).toFixed(2)}} RON</span>
                        </td>
                        <td class="nowrap">
                            <ProductMarkupPicker
                                    :item-id="product.item_id"
                                    :base-price="product.base_ron_price"
                                    :markup-type="product.markup_type"
                                    :markup="product.markup"
                                    @selected="selectItemMarkup"
                            />
                        </td>
                        <td class="nowrap">
                            <QuantityPicker
                                    :item-id="product.item_id"
                                    :initial-quantity="product.quantity"
                                    @onSelectQuantity="selectItemQuantity"
                            />

                            <template v-if="product.quantity <= product.supplier_stock">
                                <span class="small-text" style="color: darkgreen;">Stoc: {{ parseFloat(product.supplier_stock).toFixed(0) }} buc.</span>
                            </template>
                            <template v-else>
                                <span class="small-text" style="color: darkred;"><strong>Stoc: {{ parseFloat(product.supplier_stock).toFixed(0) }} buc.</strong></span>
                            </template>
                        </td>

                        <td v-if="product.quantity > 1" class="nowrap">
                            <span>{{(product.ron_unit_price * product.quantity).toFixed(2)}} RON</span><br>
                            <span class="small-text">{{product.ron_unit_price}} RON / buc</span>
                        </td>
                        <td v-else class="nowrap">
                            {{product.ron_unit_price}} RON
                        </td>

                        <td v-if="product.quantity > 1" class="nowrap">
                            <span>{{(product.ron_profit * product.quantity).toFixed(2)}} RON</span><br>
                            <span class="small-text">{{product.ron_profit}} RON / buc</span>
                        </td>
                        <td v-else class="nowrap">
                            {{product.ron_profit}} RON
                        </td>

                        <td class="nowrap">
                            <b-button
                                    variant="success"
                                    size="sm"
                                    class="mr-1"
                                    :disabled="index === 0"
                                    @click="() => moveItemUp(product.item_id)"
                            >
                                <b-icon icon="arrow-up"/>
                            </b-button>
                            <b-button
                                    variant="success"
                                    size="sm"
                                    class="mr-2"
                                    :disabled="index === offer.products.length - 1"
                                    @click="() => moveItemDown(product.item_id)"
                            >
                                <b-icon icon="arrow-down"/>
                            </b-button>

                            <b-button
                                    variant="danger"
                                    size="sm"
                                    @click="() => deleteItem(product.item_id)"
                            >
                                <b-icon icon="trash"/>
                            </b-button>
                        </td>
                    </tr>

                    </tbody>
                </table>


                <div class="total">
                    <span class="mr-3">Preț achiziție (<strong>furnizori</strong>):</span>
                    <span class="amount">
                    {{parseFloat(offer.offer.total_supplier_price).toFixed(2)}} RON
                </span>
                </div>

                <div class="total">
                    <span class="mr-3">Preț vânzare (<strong>client</strong>):</span>
                    <span class="amount">
                    {{parseFloat(offer.offer.total_price).toFixed(2)}} RON
                </span>
                </div>

                <div class="total">
                    <span class="mr-3">Profit total:</span>
                    <span class="amount">
                    {{parseFloat(offer.offer.total_profit).toFixed(2)}} RON
                </span>
                </div>
            </div>

            <div class="page">
                <h1>3. Finalizează oferta</h1>

                <form @submit="save">
                    <h2>Detalii principale</h2>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Nume client</label>
                        <div class="col-sm-4">
                            <b-form-input
                                    v-model="form.clientName"
                                    type="text"
                                    class="form-control"
                                    placeholder="Introdu numele clientului"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Titlu ofertă</label>
                        <div class="col-sm-4">
                            <b-form-input
                                    v-model="form.offerTitle"
                                    type="text"
                                    class="form-control"
                                    placeholder="(ex. Ofertă consumabile HP)"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Termen de livrare</label>
                        <div class="col-sm-4">
                            <b-form-input
                                    v-model="form.delivery"
                                    type="text"
                                    class="form-control"
                                    placeholder="(ex. 4-5 zile)"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Transport</label>
                        <div class="col-sm-4">
                            <b-form-input
                                    v-model="form.transport"
                                    type="text"
                                    class="form-control"
                                    placeholder="(ex. inclus, 19.99 RON etc.)"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Garanție</label>
                        <div class="col-sm-4">
                            <b-form-input
                                    v-model="form.warranty"
                                    type="text"
                                    class="form-control"
                                    placeholder="(ex. 24 luni)"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>

                    <h2>Alte informații</h2>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Detalii suplimentare</label>
                        <div class="col-sm-4">
                            <b-form-textarea
                                    v-model="form.details"
                                    placeholder="(ex. Achiziția poate fi efectuată prin SEAP)"
                                    rows="4"
                                    debounce="500"
                                    @update="editDetails"
                            />
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Ștampilă</label>
                        <div class="col-sm-4">
                            <b-form-radio-group
                                    v-model="form.stamp"
                                    @change="handleStampToggle"
                            >
                                <b-form-radio :value="true">Da</b-form-radio>
                                <b-form-radio :value="false">Nu</b-form-radio>
                            </b-form-radio-group>
                        </div>
                    </div>

                    <div class="d-flex justify-content-end">
                        <button type="submit" class="btn btn-primary btn-success">
                            <b-icon icon="check2-square"/>
                            Salvează oferta
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

</template>

<script>
    import CurrencyPicker from "@/components/offer/CurrencyPicker";
    import ProductMarkupPicker from "@/components/offer/ProductMarkupPicker";
    import DefaultMarkupPicker from "@/components/offer/DefaultMarkupPicker";
    import QuantityPicker from "@/components/QuantityPicker";
    import ItemNameEditor from "@/components/ItemNameEditor";
    import axios from "@/utils/axios-instance";
    import {mapState} from "vuex";

    export default {
        name: "OfferEditor",
        data() {
            return {
                isLoaded: false,
                editingOffer: null,
                orderArray: null,
                issues: null,
                form: {
                    offerTitle: "Oferta consumabile tonere ORIGINALE OEM",
                    clientName: null,
                    delivery: "1-5 zile",
                    transport: "inclus",
                    warranty: "12 luni",
                    details: "Valabilitate oferta: 30 zile",
                    stamp: true,
                },
            };
        },
        components: {ItemNameEditor, QuantityPicker, CurrencyPicker, ProductMarkupPicker, DefaultMarkupPicker},
        props: ["editId", 'inCreateMode'],
        methods: {
            async loadIssues() {
                let response = await axios.get(`offer/${this.offerId}/integrity-issues`);
                this.issues = response.data;
            },
            async loadOffer() {
                if(!this.inCreateMode) {
                    // Mark this offer as being under editing
                    let needsMarking = true;
                    this.editingOffers.forEach(offer => {
                        if (offer.id == this.offerId) {
                            needsMarking = false;
                        }
                    });

                    if (needsMarking) {
                        await axios.post(`offer/${this.offerId}/edit`);
                        await this.$store.dispatch('reloadEditingOffers');
                    }
                }

                if (this.inCreateMode) {
                    await this.$store.dispatch('reloadWorkingOffer');
                } else {
                    let response = await axios.get(`offer/${this.offerId}`);
                    this.editingOffer = response.data;
                }

                await this.loadIssues();
            },
            async reloadPage() {
                let loader = this.$loading.show();

                this.isLoaded = false;

                await this.loadOffer();

                if (this.offer.offer.extra_data) {
                    this.form = this.offer.offer.extra_data;
                }

                if (!this.orderArray) {
                    this.orderArray = this.computeOrderArray();
                }

                this.isLoaded = true;
                loader.hide();
            },

            /**
             * Offer-related API calls
             */
            async fixIssues() {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/fix-integrity-issues`);
                await this.loadOffer();

                loader.hide();
            },
            async selectExchangeRate({currency, markupType, markup}) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/set-exchange-rate-markup`, {
                    default_markup_type: markupType,
                    default_markup: markup,
                    currency,
                })
                await this.loadOffer();

                loader.hide();
            },
            async selectDefaultMarkup({markupType, markup}) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/set-markup`, {
                    default_markup_type: markupType,
                    default_markup: markup,
                })
                await this.loadOffer();

                loader.hide();
            },
            async editDetails() {
                await axios.post(`offer/${this.offerId}/set-metadata`, {
                    extra_data: JSON.stringify(this.form),
                });
            },
            handleStampToggle(value) {
                this.form.stamp = value;
                this.editDetails();
            },
            async save(evt) {
                evt.preventDefault();

                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/save`);

                await this.$store.dispatch('reloadWorkingOffer');

                if(this.offer.offer.status === 'EDITING') {
                    await this.$store.dispatch('reloadEditingOffers');
                }

                await this.$router.push(`/offer/${this.offerId}/view`);

                loader.hide();
            },

            /**
             * Offer item-related API calls
             */

            async onItemSaveName(itemId, name) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/item/${itemId}/set-custom-name`, {
                    custom_name: name,
                });
                await this.loadOffer();

                loader.hide();
            },
            async selectItemMarkup({itemId, markupType, markup}) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/item/${itemId}/set-markup`, {
                    markup_type: markupType,
                    markup: markup,
                });
                await this.loadOffer();

                loader.hide();
            },
            async selectItemQuantity({itemId, quantity}) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offer.offer.offer_id}/item/${itemId}/set-quantity`, {
                    quantity: quantity,
                });
                await this.loadOffer();

                loader.hide();
            },
            async deleteItem(itemId) {
                let loader = this.$loading.show();

                await axios.delete(`offer/${this.offerId}/item/${itemId}`);
                await this.loadOffer();

                if(this.offer.offer.status === 'EDITING') {
                    await this.$store.dispatch('reloadEditingOffers');
                }

                loader.hide();
            },

            /**
             * Offer item ordering related API calls and functions
             */

            computeOrderArray() {
                let {products} = this.offer;

                if(!products || products.length === 0) {
                    return {};
                }

                let array = {};

                products.forEach(product => {
                    let {item_id, order} = product;
                    array[item_id] = order;
                });

                return array;
            },
            async changeOrder(orderArray) {
                let loader = this.$loading.show();

                await axios.post(`offer/${this.offerId}/set-order`, {
                    order_array: orderArray,
                });

                loader.hide();
            },
            moveItemUp(item_id) {
                let myOrder = this.orderArray[item_id];

                // Find the product with the largest order, smaller than the order of the current product.
                let bestProduct = this.orderedProducts[0];
                this.orderedProducts.forEach(product => {
                    let thisOrder = this.orderArray[product.item_id];

                    if(!bestProduct && thisOrder < myOrder) {
                        bestProduct = product;
                    }

                    if(!bestProduct) {
                        return;
                    }

                    let bestOrder = this.orderArray[bestProduct.item_id];

                    if(thisOrder < myOrder && thisOrder > bestOrder) {
                        bestProduct = product;
                    }
                });

                // Swap the order!
                this.orderArray[item_id] = this.orderArray[bestProduct.item_id];
                this.orderArray[bestProduct.item_id] = myOrder;

                this.changeOrder(this.orderArray);
            },
            moveItemDown(item_id) {
                let myOrder = this.orderArray[item_id];

                // Find the product with the smallest order, larger than the order of the current product.
                let bestProduct = null;
                this.orderedProducts.forEach(product => {
                    let thisOrder = this.orderArray[product.item_id];

                    if(!bestProduct && thisOrder > myOrder) {
                        bestProduct = product;
                    }

                    if(!bestProduct) {
                        return;
                    }

                    let bestOrder = this.orderArray[bestProduct.item_id];

                    if(thisOrder > myOrder && thisOrder < bestOrder) {
                        bestProduct = product;
                    }
                });

                // Swap the order!
                this.orderArray[item_id] = this.orderArray[bestProduct.item_id];
                this.orderArray[bestProduct.item_id] = myOrder;

                this.changeOrder(this.orderArray);
            }
        },
        async created() {
            await this.reloadPage();
        },
        watch: {
            // call again the method if the route changes
            '$route': 'reloadPage'
        },
        computed: {
            offer: function() {
                if(this.inCreateMode) {
                    return this.workingOffer;
                }

                return this.editingOffer;
            },
            offerId: function() {
                if(this.inCreateMode) {
                    return this.workingOffer.offer.offer_id;
                }

                return this.editId;
            },
            orderedProducts: function() {
                let products = JSON.parse(JSON.stringify(this.offer.products));

                if(!this.orderArray) {
                    return [];
                }

                products.sort((productA, productB) => {
                    let orderA = this.orderArray[productA.item_id];
                    let orderB = this.orderArray[productB.item_id];

                    return orderA - orderB;
                });

                return products;
            },
            hasIssues() {
                if(!this.issues) return false;

                if(this.issues.changed_currencies.length > 0) return true;
                if(this.issues.changed_offer_items.length > 0) return true;
                if(this.issues.unavailable_offer_items.length > 0) return true;

                return false;
            },
            ...mapState(['editingOffers', 'workingOffer']),
        }
    }
</script>

<style scoped lang="scss">
.page {
  background: white;
  border-radius: 5px;
  color: black;
  padding: 25px 20px;

  margin-top: 50px;
  margin-bottom: 20px;

  -webkit-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  -moz-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);

  p {
    margin: 0;
  }

  h1 {
    color: #024A8C;
    font-weight: 600;
  }

  h2, h3, h4, h5, h6 {
    color: #6F6F6F;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  table th {
    color: #024A8C;
    font-weight: 600;
  }
}

.red.page {
  background: darkred;
  color: #e8e8e8;

  h1, h2, h3, h4, h5, h6 {
    color: #FFFFFF;
  }

  table th {
    color: #dadada;
  }

  table td {
    color: #FFFFFF;
  }
}

h1 {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}

h3 {
  font-size: 1rem;
}

table td {
  white-space: normal;
  vertical-align: middle;
  word-break: break-word;
}

table td.nowrap {
  width: 1%;
  white-space: nowrap;
}

.small-text {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes > span::after {
  content: '; ';
}

.total {
  color: #024A8C;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.total .amount {
  font-weight: 800;
  font-size: 1.3rem;
}

.currencies div {
  margin-bottom: 5px;
}
</style>